import { useEffect, useCallback, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useUnsavedChangesConfirmation } from '@/shared/contexts/UnsavedChangesContext';

/**
 * Options for useUnsavedChanges hook
 */
interface UseUnsavedChangesOptions {
  /** Whether the form has unsaved changes */
  isDirty: boolean;
  /** Custom confirmation message (optional) */
  message?: string;
  /** Whether to enable blocking (optional, defaults to true) */
  enabled?: boolean;
  /**
   * When provided and its `.current` is `true`, navigation blocking is
   * skipped synchronously (useful to bypass stale-closure issues after
   * programmatic form submission).
   */
  overrideRef?: { current: boolean };
}

/**
 * Hook to warn users about unsaved changes when navigating away
 * 
 * This hook uses the global UnsavedChangesModal via context
 * to warn users before they leave a page with unsaved changes.
 * 
 * @example
 * ```tsx
 * const [isDirty, setIsDirty] = useState(false);
 * 
 * useUnsavedChanges({
 *   isDirty,
 *   message: 'You have unsaved changes. Do you want to leave without saving?'
 * });
 * ```
 */
export const useUnsavedChanges = (
  options: UseUnsavedChangesOptions
): void => {
  const { confirmUnsavedChanges, dismissUnsavedChanges } = useUnsavedChangesConfirmation();
  const {
    isDirty,
    message = 'You have unsaved changes. Do you want to leave without saving?',
    enabled = true,
    overrideRef,
  } = options;
  const isHandlingBlockRef = useRef(false);

  // Block navigation when there are unsaved changes.
  // NOTE: overrideRef.current is read at call-time (not closure-time), so a
  // synchronous ref update in the caller's event handler takes effect before
  // the blocker callback fires.
  const blocker = useBlocker(
    useCallback(() => {
      if (!enabled || !isDirty || overrideRef?.current) {
        return false;
      }
      return true;
    }, [isDirty, enabled, overrideRef])
  );

  // Handle the blocker state
  useEffect(() => {
    if (blocker.state !== 'blocked' || isHandlingBlockRef.current) {
      return;
    }

    let isActive = true;
    isHandlingBlockRef.current = true;

    void confirmUnsavedChanges({ message })
      .then((userConfirmed) => {
        if (!isActive) {
          return;
        }

        if (userConfirmed) {
          blocker.proceed();
        } else {
          blocker.reset();
        }
      })
      .finally(() => {
        isHandlingBlockRef.current = false;
      });

    return () => {
      isActive = false;
    };
  }, [blocker, confirmUnsavedChanges, message]);

  useEffect(() => {
    if (enabled && isDirty) {
      return;
    }

    dismissUnsavedChanges();

    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker, dismissUnsavedChanges, enabled, isDirty]);

  // Handle browser close/refresh
  useEffect(() => {
    if (!enabled || !isDirty) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Modern browsers require returnValue to be set
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, message, enabled]);
};

export default useUnsavedChanges;
